import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  List,
  ListItemButton,
  ListItemText,
  Box,
  Divider,
  Paper,
  ThemeProvider,
  createTheme,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import "./SideDrawer.css";
import { hasRole, hasUamAdminAccess } from "../../utils/userAccess";
import { fetchAdminAccessRecords } from "../../services/OutboundOrders/UserRolesServices";
import { useMsal } from "@azure/msal-react";

const drawerWidth = 75;
const submenuWidth = 175;
const itemHeight = 40;
const sidebarSelectedBg = "#6AC9CE";
const sidebarSelectedText = "#FFFFFF";
const sidebarHoverBg = "#6AC9CE";
const sidebarHoverText = "#FFFFFF";

const SideDrawer = ({ options = [], contentHeight = 0, flat = false, roles = [] }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [submenuTop, setSubmenuTop] = useState(0);
  const [hasDbAdminAccess, setHasDbAdminAccess] = useState(false);

  const drawerRef = useRef(null);
  const submenuRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { accounts } = useMsal();
  const currentUserEmail = (accounts?.[0]?.username || accounts?.[0]?.name || "")
    .toLowerCase()
    .trim();

  // Existing admin evaluation
  const isAdminUser = hasUamAdminAccess(roles);
  const visibleOptions = useMemo(
    () => options.filter((item) => !item.requiredRole || hasRole(roles, item.requiredRole)),
    [options, roles]
  );

  // Check database `admin_access` table for the logged-in user
  useEffect(() => {
    const verifyDbAccess = async () => {
      if (!currentUserEmail) return;
      try {
        const records = await fetchAdminAccessRecords();
        const allRecords = Array.isArray(records) ? records : [];
        const isAssignedInDb = allRecords.some(
          (row) =>
            row.adminEmail &&
            row.adminEmail.toLowerCase().trim() === currentUserEmail
        );
        setHasDbAdminAccess(isAssignedInDb);
      } catch (err) {
        console.error("Failed to verify DB admin access in SideDrawer:", err);
        setHasDbAdminAccess(false);
      }
    };

    verifyDbAccess();
  }, [currentUserEmail]);

  /**
   * Evaluates if a navigation item should be disabled.
   */
  const checkIsDisabled = (item) => {
    if (!item) return false;

    if (item.requiredRole && !hasRole(roles, item.requiredRole)) {
      return true;
    }

    const itemId = String(item.id || "").toLowerCase();
    const itemName = String(item.name || "").toLowerCase();
    const itemPath = String(item.path || "").toLowerCase();

    // 1. Admin Portal screen (Strictly AD / JWT role-based)
    if (
      itemId === "access-control" ||
      itemName === "admin portal" ||
      itemPath.includes("access-control")
    ) {
      return !isAdminUser;
    }

    // 2. Approver Configuration screen (Strictly database table lookup)
    if (
      itemId.includes("approver") ||
      itemName.includes("approver") ||
      itemPath.includes("approver")
    ) {
      return !hasDbAdminAccess;
    }

    // Default fallback: exactly matches dev behavior for all other items
    return Boolean(item.adminOnly && !isAdminUser);
  };

  const theme = createTheme({
    typography: {
      fontSize: 11,
    },
  });

  // Track active state based on current route (adapted from coke-ct-ui)
  useEffect(() => {
    const currentPath = location.pathname;
    for (const item of visibleOptions) {
      // Check if current path matches any sub-rule's path
      if (item.rules?.length) {
        const matchedRule = item.rules.find(
          (rule) => rule.path && (currentPath.startsWith(rule.path) || currentPath.endsWith("/" + rule.path))
        );
        if (matchedRule) {
          setSelectedLabel(matchedRule.name);
          setSelectedCategory(item.id);
          return;
        }
      }
      // Check if current path matches the item's direct path
      if (item.path && item.path !== "/") {
        if (currentPath.startsWith(item.path) || currentPath.endsWith("/" + item.path)) {
          setSelectedLabel(item.name);
          setSelectedCategory(null);
          return;
        }
      } else if (item.path === "/" && currentPath === "/") {
        setSelectedLabel(item.name);
        setSelectedCategory(null);
        return;
      }
    }
    // If on a base path like /supply-planning (index route), highlight first rule of first category
    for (const item of visibleOptions) {
      if (item.rules?.length && item.rules[0]?.path) {
        setSelectedLabel(item.rules[0].name);
        setSelectedCategory(item.id);
        return;
      }
    }
    // Default to Home if on root
    if (currentPath === "/") {
      setSelectedLabel("Home");
      setSelectedCategory(null);
    }
  }, [location.pathname, visibleOptions]);

  const handleRedirect = (label, path) => {
    if (label === "Home") {
      navigate("/");
      return;
    }
    navigate(path || "/");
    setShowSubmenu(false);
  };

  // Close submenu on outside click (reused from coke-ct-ui)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        submenuRef.current &&
        !submenuRef.current.contains(event.target) &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setShowSubmenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCategoryClick = (e, item) => {
    // Items with a direct path and no sub-rules navigate directly
    if (item.path && (!item.rules || item.rules.length === 0)) {
      setSelectedLabel(item.name);
      setSelectedCategory(null);
      handleRedirect(item.name, item.path);
      return;
    }

    // Items with sub-rules open the submenu (adapted from coke-ct-ui positioning logic)
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetTop = rect?.top || 0;
    const drawerTop = contentHeight || 0;
    const estimatedHeight = (item.rules?.length || 0) * itemHeight;
    const availableSpaceBelow = window.innerHeight - offsetTop;
    const availableSpaceAbove = offsetTop - drawerTop;
    let adjustedTop = offsetTop - drawerTop;

    if (
      estimatedHeight > availableSpaceBelow &&
      availableSpaceAbove > availableSpaceBelow
    ) {
      adjustedTop = Math.max(
        0,
        offsetTop - drawerTop - estimatedHeight + itemHeight
      );
    } else if (adjustedTop + estimatedHeight > window.innerHeight - drawerTop) {
      adjustedTop = Math.max(
        0,
        window.innerHeight - drawerTop - estimatedHeight
      );
    }

    if (selectedCategory === item.id && showSubmenu) {
      setShowSubmenu(false);
      setSelectedCategory(null);
    } else {
      setSelectedCategory(item.id);
      setShowSubmenu(true);
      setSubmenuTop(adjustedTop);
    }
  };

  const computeAndOpenSubmenu = (e, item) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetTop = rect?.top || 0;
    const drawerTop = contentHeight || 0;
    const estimatedHeight = (item.rules?.length || 0) * itemHeight;
    const availableSpaceBelow = window.innerHeight - offsetTop;
    const availableSpaceAbove = offsetTop - drawerTop;
    let adjustedTop = offsetTop - drawerTop;

    if (
      estimatedHeight > availableSpaceBelow &&
      availableSpaceAbove > availableSpaceBelow
    ) {
      adjustedTop = Math.max(0, offsetTop - drawerTop - estimatedHeight + itemHeight);
    } else if (adjustedTop + estimatedHeight > window.innerHeight - drawerTop) {
      adjustedTop = Math.max(0, window.innerHeight - drawerTop - estimatedHeight);
    }

    setSelectedCategory(item.id);
    setShowSubmenu(true);
    setSubmenuTop(adjustedTop);
  };

  const handleItemMouseEnter = (e, item) => {
    if (!Array.isArray(item.rules) || item.rules.length === 0) return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    computeAndOpenSubmenu(e, item);
  };

  const handleItemMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setShowSubmenu(false), 200);
  };

  const handleSubmenuMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };

  const handleSubmenuMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setShowSubmenu(false), 200);
  };

  const currentSubOptions = selectedCategory
    ? visibleOptions.find((opt) => opt.id === selectedCategory)?.rules || []
    : [];

  const renderNavButton = (label, icon, isActive, onClick, disabled = false, extraProps = {}) => {
    const showActive = isActive && !disabled;

    return (
      <ListItemButton
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        {...extraProps}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "80px !important",
          minHeight: 80,
          maxHeight: 80,
          py: 0,
          px: 0,
          backgroundColor: showActive ? sidebarSelectedBg : "transparent",
          transition: "background-color 0.2s",
          opacity: disabled ? 0.4 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          pointerEvents: disabled ? "none" : "auto",
          "&:hover": {
            backgroundColor: disabled ? "transparent" : sidebarHoverBg,
            "& .nav-icon, & .nav-label": {
              color: disabled ? "#000" : sidebarHoverText,
            },
          },
        }}
      >
        <Box
          className="nav-icon"
          sx={{
            color: showActive ? sidebarSelectedText : "#000",
            mb: 0.5,
            "& svg": { fontSize: "1.35rem" },
          }}
        >
          {icon}
        </Box>
        <Typography
          className="nav-label"
          variant="caption"
          sx={{
            fontSize: "0.58rem",
            color: showActive ? sidebarSelectedText : "#000",
            textAlign: "center",
            lineHeight: 1.1,
          }}
        >
          {label}
        </Typography>
      </ListItemButton>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        id="side-drawer"
        ref={drawerRef}
        sx={{
          width: drawerWidth,
          minWidth: drawerWidth,
          backgroundColor: "#bcbcbc",
          color: "#fff",
          position: "fixed",
          top: contentHeight || 0,
          left: 0,
          bottom: 28,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justify: "flex-start",
          pt: 0.5,
          pb: "10px",
          zIndex: 1200,
          overflowY: "auto",
          scrollbarWidth: "thin",
        }}
      >
        {flat ? (
          /* Flat mode: direct nav for leaf items, hover submenu for items with rules */
          visibleOptions.map((item) => {
            const isDisabled = checkIsDisabled(item);
            const hasRules = Array.isArray(item.rules) && item.rules.length > 0;
            const isActive = hasRules
              ? item.rules.some((r) => location.pathname.startsWith(r.path))
              : item.path === location.pathname;
            return (
              <React.Fragment key={item.id}>
                {renderNavButton(
                  item.name,
                  item.icon,
                  isActive,
                  hasRules ? undefined : () => handleRedirect(item.name, item.path),
                  isDisabled,
                  hasRules ? {
                    onMouseEnter: (e) => handleItemMouseEnter(e, item),
                    onMouseLeave: handleItemMouseLeave,
                  } : {}
                )}
              </React.Fragment>
            );
          })
        ) : (
          <>
            {/* Home button */}
            {renderNavButton(
              "Home",
              <HomeIcon />,
              selectedLabel === "Home",
              () => handleRedirect("Home", "/")
            )}
            <Divider sx={{ width: "100%", borderColor: "#888", mt: 0 }} />

            {/* Menu items */}
            {visibleOptions.map((item, idx) => {
              return (
                <React.Fragment key={item.id}>
                  {renderNavButton(
                    item.name,
                    item.icon,
                    selectedCategory === item.id || selectedLabel === item.name,
                    (e) => handleCategoryClick(e, item),
                    checkIsDisabled(item)
                  )}
                  {idx !== visibleOptions.length - 1 && (
                    <Divider sx={{ width: "100%", borderColor: "#888", mt: 0 }} />
                  )}
                </React.Fragment>
              );
            })}
          </>
        )}
      </Box>

      {/* Submenu popup (reused from coke-ct-ui) */}
      {showSubmenu && selectedCategory && currentSubOptions.length > 0 && (
        <Paper
          elevation={4}
          ref={submenuRef}
          onMouseEnter={handleSubmenuMouseEnter}
          onMouseLeave={handleSubmenuMouseLeave}
          sx={{
            position: "fixed",
            top: submenuTop + (contentHeight || 0),
            left: drawerWidth,
            width: submenuWidth,
            borderRadius: 1,
            overflow: "hidden",
            zIndex: 1500,
          }}
        >
          <List disablePadding className="submenu-panel">
            {currentSubOptions.map((rule) => {
              const isSubmenuActive = location.pathname.startsWith(rule.path);
              return (
                <ListItemButton
                  key={rule.name}
                  className="submenu-item"
                  onClick={() => handleRedirect(rule.name, rule.path)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 1,
                    backgroundColor: isSubmenuActive ? sidebarSelectedBg : "transparent",
                    color: isSubmenuActive ? sidebarSelectedText : "#000",
                    "&:hover": {
                      backgroundColor: sidebarHoverBg,
                      color: sidebarHoverText,
                    },
                  }}
                >
                  <ListItemText primary={rule.name} />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      )}
    </ThemeProvider>
  );
};

export default SideDrawer;
